import { Slot } from '@radix-ui/react-slot'
import React, { useContext } from 'react'

import { HeadlessListbox } from 'slab/components/HeadlessListbox'
import { Popper } from 'slab/components/Popper'

import { AutocompleteContext } from './context'

interface AutocompleteInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  asChild?: boolean;
  /**
   * Whether to open the autocomplete dropdown when the input is focused.
   *
   * By default this is disabled. Opening the dropdown on focus can be annoying
   * for keyboard users who are merely trying to move focus past the input to
   * another element, especially if the dropdown contents are long. When
   * disabled, the dropdown will only open when text is entered or when the
   * down arrow is pressed.
   *
   * There may be instances where it _is_ better to open the dropdown immediately
   * on focus, so this option is configurable. For example, if the dropdown
   * contains helpful context about the input and its options, it may be better
   * to open it immediately.
   */
  openOnFocus?: boolean;
}

export function AutocompleteInput ({ asChild, openOnFocus = false, ...props }: AutocompleteInputProps) {
  const { inputRef, listId, open, setOpen } = useContext(AutocompleteContext)

  const Component = asChild ? Slot : 'input'

  return (
    <HeadlessListbox.Control asChild>
      <Popper.Anchor
        asChild
        getAnchorRelativeToRef={(element) => {
          const parentElement = element?.parentElement

          return parentElement?.matches(`[data-form-field-for="${element?.id}"]`)
            ? parentElement
            : element
        }}
      >
        <Component
          {...props}
          aria-autocomplete='list'
          aria-controls={listId}
          aria-expanded={open}
          onChange={() => {
            if (!open) setOpen(true)
          }}
          onClick={() => {
            if (!open) setOpen(true)
          }}
          onFocus={() => {
            if (openOnFocus && !open) setOpen(true)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false)
            }

            if (event.key === 'ArrowDown' && !open) {
              setOpen(true)
            }
          }}
          ref={inputRef}
          role='combobox'
        />
      </Popper.Anchor>
    </HeadlessListbox.Control>
  )
}

AutocompleteInput.displayName = 'Autocomplete.Input'
